/* assets/wbs-cart-recommendations.js
   Cart upsell carousel. Source is set in Theme settings → WBS: Cart Upsell.

   complementary (default):
   - Uses each cart product's Search & Discovery complementary list
     (shopify--discovery--product_recommendation.complementary_products)
   - Cart order, then the order saved on the product
   - Skips products already in the cart and hides when none remain

   recommendations:
   - Shopify complementary, then related, then the fallback collection, then the catalog

   One product visible at a time. Add to cart via Shopify.actions.updateCart.
*/

// @ts-nocheck
import { formatMoney } from '@theme/money-formatting';

(() => {
  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this._index = 0;
      this._items = [];
      this._loading = false;
      this._lastFetchKey = '';
      this._complementaryRaw = '';
      this._observer = null;

      this._onClick = this._onClick.bind(this);
      this._onCartUpdate = this._onCartUpdate.bind(this);
    }

    get track() {
      return this.querySelector('[data-cd-track]');
    }

    get viewport() {
      return this.querySelector('[data-cd-viewport]');
    }

    connectedCallback() {
      if (!this.querySelector('[data-cd-viewport]') || !this.querySelector('[data-cd-track]')) return;

      this.prevBtn = this.querySelector('[data-cd-prev]');
      this.nextBtn = this.querySelector('[data-cd-next]');

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this._index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this._index + 1));

      this.addEventListener('click', this._onClick);
      this._bindSwipe();

      this._setupObserver();
      this.load();

      document.addEventListener('shopify:cart:lines-update', this._onCartUpdate);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      document.removeEventListener('shopify:cart:lines-update', this._onCartUpdate);
      this._observer?.disconnect();
      this._observer = null;
    }

    _isComplementarySource() {
      return (this.dataset.source || 'complementary') !== 'recommendations';
    }

    _complementaryScriptText() {
      return (this.querySelector('[data-cd-complementary]')?.textContent || '').trim();
    }

    _readComplementaryProducts() {
      const raw = this._complementaryScriptText();
      this._complementaryRaw = raw;
      if (!raw) return [];

      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    }

    _applyComplementary(cartProductIds) {
      const limit = parseInt(this.dataset.limit || '10', 10);
      const seenIds = new Set();
      const products = [];

      for (const prod of this._readComplementaryProducts()) {
        if (!prod || !prod.id) continue;
        const pidStr = String(prod.id);
        if (cartProductIds.has(pidStr) || seenIds.has(pidStr)) continue;
        seenIds.add(pidStr);
        products.push(prod);
        if (products.length >= limit) break;
      }

      if (!products.length) {
        this._hide();
        return;
      }

      this._items = products;
      if (this.track) this.track.innerHTML = products.map((p) => this._renderSlide(p)).join('');
      this._show();
      this.goTo(0);
      this._updateNav();
    }

    _hide() {
      this.classList.add('is-hidden');
      this.hidden = true;
      this.style.display = 'none';
      if (this.track) this.track.innerHTML = '';
      this._items = [];
    }

    _show() {
      this.classList.remove('is-hidden');
      this.hidden = false;
      this.style.display = '';
    }

    _setupObserver() {
      this._observer?.disconnect();

      this._observer = new MutationObserver(() => {
        const track = this.track;
        if (!track) return;

        if (this._isComplementarySource()) {
          const raw = this._complementaryScriptText();
          if (raw !== this._complementaryRaw) {
            this._observer.disconnect();
            const cartProductIds = new Set(
              (this.dataset.productIds || '')
                .split(',')
                .map((id) => id.trim())
                .filter(Boolean)
            );
            this._applyComplementary(cartProductIds);
            Promise.resolve().then(() => {
              if (this._observer) {
                this._observer.observe(this, { childList: true, subtree: true, characterData: true });
              }
            });
            return;
          }
        }

        // If track was emptied by morph but we have cached items, re-render immediately
        if (track.children.length === 0 && this._items.length > 0) {
          this._observer.disconnect();

          track.innerHTML = this._items.map((p) => this._renderSlide(p)).join('');

          const safeIndex = Math.min(this._index, this._items.length - 1);
          track.style.transform = `translateX(-${safeIndex * 100}%)`;
          this._updateNav();

          Promise.resolve().then(() => {
            if (this._observer) {
              this._observer.observe(this, { childList: true, subtree: true, characterData: true });
            }
          });
        }
      });

      this._observer.observe(this, { childList: true, subtree: true, characterData: true });
    }

    async _onCartUpdate() {
      await this.load(true);
    }

    async load(force = false) {
      if (force) {
        this._loading = false;
      } else if (this._loading) {
        return;
      }
      this._loading = true;

      try {
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!cartRes.ok) {
          this._hide();
          return;
        }

        const cartJson = await cartRes.json();
        const cartItems = cartJson?.items || [];

        if (!cartItems.length) {
          this._hide();
          return;
        }

        // Collect all product IDs currently in the cart
        const cartProductIds = new Set(cartItems.map((item) => String(item.product_id)));
        const uniqueCartProductIds = [...cartProductIds];

        if (this._isComplementarySource()) {
          this._applyComplementary(cartProductIds);
          return;
        }

        const fetchKey = uniqueCartProductIds.sort().join(',');
        if (!force && this._lastFetchKey === fetchKey && this._items.length > 0) {
          // Re-filter cached items against current cart
          const remaining = this._items.filter((p) => p && p.id && !cartProductIds.has(String(p.id)));
          if (remaining.length > 0) {
            this._items = remaining;
            this.track.innerHTML = remaining.map((p) => this._renderSlide(p)).join('');
            this.goTo(Math.min(this._index, remaining.length - 1));
            this._show();
            return;
          }
        }
        this._lastFetchKey = fetchKey;

        const root = window.Shopify?.routes?.root || window.routes?.root || '/';
        const limit = parseInt(this.dataset.limit || '10', 10);
        const fallbackCollectionHandle = this.dataset.fallbackCollectionHandle;

        // 1. Fetch complementary products for all cart items
        const complementaryPromises = uniqueCartProductIds.map(async (pid) => {
          try {
            const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(pid)}&limit=${encodeURIComponent(limit)}&intent=complementary`;
            const res = await fetch(url, { credentials: 'same-origin' });
            if (!res.ok) return [];
            const data = await res.json();
            return data?.products || [];
          } catch (_) {
            return [];
          }
        });

        // 2. Fetch related products for all cart items
        const relatedPromises = uniqueCartProductIds.map(async (pid) => {
          try {
            const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(pid)}&limit=${encodeURIComponent(limit)}&intent=related`;
            const res = await fetch(url, { credentials: 'same-origin' });
            if (!res.ok) return [];
            const data = await res.json();
            return data?.products || [];
          } catch (_) {
            return [];
          }
        });

        // 3. Fetch fallback collection products if specified
        const fallbackPromise = (async () => {
          if (!fallbackCollectionHandle) return [];
          try {
            const url = `${root}collections/${encodeURIComponent(fallbackCollectionHandle)}/products.json?limit=50`;
            const res = await fetch(url, { credentials: 'same-origin' });
            if (!res.ok) return [];
            const data = await res.json();
            return data?.products || [];
          } catch (_) {
            return [];
          }
        })();

        // 4. Fetch all store products as catalog fallback
        const allProductsPromise = (async () => {
          try {
            const url = `${root}products.json?limit=50`;
            const res = await fetch(url, { credentials: 'same-origin' });
            if (!res.ok) return [];
            const data = await res.json();
            return data?.products || [];
          } catch (_) {
            return [];
          }
        })();

        const [complementaryResults, relatedResults, fallbackProducts, allStoreProducts] = await Promise.all([
          Promise.all(complementaryPromises),
          Promise.all(relatedPromises),
          fallbackPromise,
          allProductsPromise,
        ]);

        // Merge in strict priority:
        // Priority 1: Complementary products of each cart item (in cart order)
        // Priority 2: Related products of each cart item (in cart order)
        // Priority 3: Fallback collection products
        // Priority 4: All store catalog products
        const seenIds = new Set();
        const candidateProducts = [];

        const addCandidates = (list) => {
          for (const prod of list) {
            if (!prod || !prod.id) continue;
            const pidStr = String(prod.id);
            // Must NOT be already in the cart, and not duplicate in recommendations
            if (!cartProductIds.has(pidStr) && !seenIds.has(pidStr)) {
              seenIds.add(pidStr);
              candidateProducts.push(prod);
            }
          }
        };

        // Add complementary products
        for (const list of complementaryResults) {
          addCandidates(list);
        }

        // Add related products if we need more
        if (candidateProducts.length < limit) {
          for (const list of relatedResults) {
            addCandidates(list);
          }
        }

        // Add fallback collection products if we need more
        if (candidateProducts.length < limit && fallbackProducts.length > 0) {
          addCandidates(fallbackProducts);
        }

        // Add all store products if we still need more
        if (candidateProducts.length < limit && allStoreProducts.length > 0) {
          addCandidates(allStoreProducts);
        }

        const finalProducts = candidateProducts.slice(0, limit);

        // If no unpurchased products remain (all products in cart), strictly hide the entire block
        if (!finalProducts.length) {
          this._hide();
          return;
        }

        this._items = finalProducts;
        this.track.innerHTML = finalProducts.map((p) => this._renderSlide(p)).join('');
        this._show();
        this.goTo(0);
        this._updateNav();
      } catch (e) {
        console.error('[cart-recommendations] load failed', e);
        this._hide();
      } finally {
        this._loading = false;
      }
    }

    _renderSlide(p) {
      try {
        const title = this._esc(p.title || 'Untitled Product');
        const url = p.url || (p.handle ? `/products/${p.handle}` : '#');

        const ratioMode = this.dataset.imageRatio || 'square';
        let ratio = 1;
        if (ratioMode === 'portrait') {
          ratio = 0.8;
        } else if (ratioMode === 'adapt') {
          const w = Number(p.featured_image_width || p.image?.width || p.images?.[0]?.width || 0);
          const h = Number(p.featured_image_height || p.image?.height || p.images?.[0]?.height || 0);
          if (w > 0 && h > 0) ratio = w / h;
        }

        let img = '';
        if (p.featured_image) {
          img = `<img src="${p.featured_image}" class="cart-items__media-image" alt="${title}" loading="lazy" width="250" height="${Math.round(250 / ratio)}">`;
        } else if (p.image && p.image.src) {
          img = `<img src="${p.image.src}" class="cart-items__media-image" alt="${title}" loading="lazy" width="250" height="${Math.round(250 / ratio)}">`;
        } else if (p.images && p.images.length > 0) {
          const imgSrc = typeof p.images[0] === 'string' ? p.images[0] : p.images[0].src;
          img = `<img src="${imgSrc}" class="cart-items__media-image" alt="${title}" loading="lazy" width="250" height="${Math.round(250 / ratio)}">`;
        }

        const priceCents =
          typeof p.price === 'number'
            ? p.price
            : typeof p.price === 'string' && p.price.trim() !== '' && !isNaN(Number(p.price))
              ? Number(p.price)
              : p.price_min
                ? p.price_min
                : null;
        const price = priceCents != null ? this._formatMoney(priceCents) : '';

        const variantId = p?.variants?.[0]?.id;
        const productId = p?.id;
        const productIdAttr = productId ? ` data-product-id="${productId}"` : '';

        return `
        <div class="cd-rec__slide border-style">
          <div class="cd-rec__item">
            <a class="cd-rec__img cart-items__media-container" href="${url}" aria-label="${title}" style="--ratio:${ratio};">${img}</a>
            <div class="cd-rec__meta">
              <p class="cd-rec__name cart-items__title"><a href="${url}">${title}</a></p>
              <div class="cd-rec__price-row">
                <p class="cd-rec__price cart-primary-typography cart-items__unit-price-wrapper">${price}</p>
                ${
                  variantId
                    ? `<button type="button" class="button button--primary cd-rec__add" data-cd-add data-variant-id="${variantId}"${productIdAttr}>
                       Add
                     </button>`
                    : ``
                }
              </div>
            </div>
          </div>
        </div>
      `;
      } catch (e) {
        return `<div class="cd-rec__slide"><div class="cd-rec__item">Error rendering product</div></div>`;
      }
    }

    goTo(i) {
      const max = this._items.length - 1;
      if (max < 0) return;

      if (i < 0) i = max;
      if (i > max) i = 0;

      this._index = i;
      this.track.style.transform = `translateX(-${this._index * 100}%)`;
      this._updateNav();
    }

    _updateNav() {
      const max = this._items.length - 1;
      const multi = max >= 1;

      if (this.prevBtn) this.prevBtn.disabled = !multi;
      if (this.nextBtn) this.nextBtn.disabled = !multi;
    }

    async _onClick(e) {
      const btn = e.target.closest('[data-cd-add]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      try {
        await window.Shopify.actions.updateCart({
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
        });

        btn.textContent = 'Added';
        setTimeout(() => (btn.textContent = original), 900);

        // Immediate reload of complementary items with newly added item filtered out
        await this.load(true);
      } catch (err) {
        console.error('[cart-recommendations] add failed', err);
        btn.textContent = 'Error';
        setTimeout(() => (btn.textContent = original), 1200);
      } finally {
        btn.disabled = false;
      }
    }

    _bindSwipe() {
      const vp = this.viewport;
      if (!vp) return;

      let startX = 0;
      let currentX = 0;
      let dragging = false;

      const start = (x) => {
        dragging = true;
        startX = x;
        currentX = x;
      };

      const move = (x) => {
        if (!dragging) return;
        currentX = x;
      };

      const end = () => {
        if (!dragging) return;
        dragging = false;

        const dx = currentX - startX;
        if (Math.abs(dx) < 35) return;
        if (dx < 0) this.goTo(this._index + 1);
        else this.goTo(this._index - 1);
      };

      vp.addEventListener('touchstart', (e) => start(e.touches[0].clientX), { passive: true });
      vp.addEventListener('touchmove', (e) => move(e.touches[0].clientX), { passive: true });
      vp.addEventListener('touchend', end);

      vp.addEventListener('mousedown', (e) => {
        e.preventDefault();
        start(e.clientX);
      });
      window.addEventListener('mousemove', (e) => move(e.clientX));
      window.addEventListener('mouseup', end);
    }

    _formatMoney(cents) {
      try {
        const moneyFormat = this.dataset?.moneyFormat || '{{amount}}';
        const currency = this.dataset?.currency || window.theme?.currency || 'USD';
        return formatMoney(Number(cents), moneyFormat, currency);
      } catch (e) {
        return '$' + (Number(cents) / 100).toFixed(2);
      }
    }

    _esc(str) {
      return String(str || '').replace(/[&<>"']/g, (m) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[m]));
    }
  }

  if (!customElements.get('cart-recommendations')) {
    customElements.define('cart-recommendations', CartRecommendations);
  }
})();